import React, { useState, useEffect } from 'react';
import { claimSwipe, releaseSwipe } from '../utils/swipeOwner';
import EventIcon, { iconOf, stripIcon } from './EventIcon';
import { uses12h, weekStartsOn } from '../utils/timeFormat';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, subMonths, addDays,
  isToday, isSameMonth, isSameDay, parseISO,
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import apiClient from '../api/client';
import { monthCountsCache } from '../utils/viewCache';
import { a11y } from '../utils/a11yLabels';

// "Pallini" and "Titoli" described the drawing; these describe how much you
// are being shown, which is the actual choice. Silvia's pick.
const VIEW_WORDS = {
  it:      { dots: 'Compatta',  titles: 'Estesa' },
  en:      { dots: 'Compact',   titles: 'Expanded' },
  es:      { dots: 'Compacta',  titles: 'Ampliada' },
  de:      { dots: 'Kompakt',   titles: 'Erweitert' },
  fr:      { dots: 'Compacte',  titles: 'Étendue' },
  nl:      { dots: 'Compact',   titles: 'Uitgebreid' },
  pt:      { dots: 'Compacta',  titles: 'Alargada' },
  'pt-BR': { dots: 'Compacta',  titles: 'Expandida' },
  ru:      { dots: 'Компактно', titles: 'Подробно' },
  pl:      { dots: 'Zwarty',    titles: 'Rozszerzony' },
  ro:      { dots: 'Compact',   titles: 'Extins' },
  zh:      { dots: '紧凑',       titles: '展开' },
  ja:      { dots: 'コンパクト',  titles: '詳細' },
  hi:      { dots: 'संक्षिप्त',    titles: 'विस्तृत' },
  ko:      { dots: '간단히',      titles: '자세히' },
  ar:      { dots: 'مضغوط',     titles: 'موسّع' },
};

export default function MonthView({ onSelectDate, selectedDate, locale, events = [], loading = false, onGoToDay }) {
  const { user } = useAuth();
  const T = useT(user?.language);
  // Spoken names for the two arrows below, whose only content is a chevron.
  const A = a11y(user?.language);
  const navigate = useNavigate();

  const initDate = selectedDate ? parseISO(selectedDate) : new Date();
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(initDate));
  const [eventCounts, setEventCounts] = useState({}); // { "YYYY-MM-DD": n }
  // Two ways of reading a month, and Silvia wanted both rather than one:
  // ours — a dot and a number, then the whole day underneath when you tap it —
  // and the one Jodi's calendar draws, every square packed with titles. Hers
  // answers "what is on the 14th?" without a tap; ours answers "which days are
  // busy?" without reading anything. The choice is remembered.
  const [dense, setDense] = useState(() => {
    try { return localStorage.getItem('memo_month_dense') === '1'; } catch (_) { return false; }
  });
  const [monthEvents, setMonthEvents] = useState({});   // { "YYYY-MM-DD": [ev] }
  const [denseLoading, setDenseLoading] = useState(false);

  const calStart = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: weekStartsOn() });
  const calEnd   = endOfWeek(endOfMonth(currentMonth),     { weekStartsOn: weekStartsOn() });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });
  const numRows = days.length / 7;

  const dayHeaders = Array.from({ length: 7 }, (_, i) =>
    format(addDays(calStart, i), 'EEEEE', { locale })
  );

  const selectedObj = selectedDate ? parseISO(selectedDate) : null;
  const userLang = user?.language || 'it';
  const use12h = uses12h(userLang);

  // The titles, only when they are being looked at: nobody who keeps the dots
  // should pay for a month of events on every swipe.
  useEffect(() => {
    if (!dense) return undefined;
    let cancelled = false;
    const from = format(calStart, 'yyyy-MM-dd');
    const to   = format(calEnd, 'yyyy-MM-dd');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setDenseLoading(true);
    apiClient.get('/events/month', { params: { month: format(currentMonth, 'yyyy-MM'), detail: 1, from, to, tz } })
      .then(res => {
        if (cancelled) return;
        const byDay = {};
        for (const ev of (res.data?.events || [])) {
          const key = String(ev.start || '').slice(0, 10);
          if (!key) continue;
          (byDay[key] = byDay[key] || []).push(ev);
        }
        setMonthEvents(byDay);
      })
      .catch(() => { if (!cancelled) setMonthEvents({}); })
      .finally(() => { if (!cancelled) setDenseLoading(false); });
    return () => { cancelled = true; };
  }, [dense, currentMonth]);   // eslint-disable-line

  // A sideways drag moves the month, exactly as it moves the day and the
  // week. Same thresholds, same refusal to touch the vertical scroll.
  useEffect(() => {
    claimSwipe('month');
    let x0 = 0, y0 = 0, t0 = 0, live = false;
    function start(e) {
      const t = e.touches && e.touches[0];
      if (!t) { live = false; return; }
      if (e.target?.closest?.('button, a, input, textarea, select, [role="button"]')) { live = false; return; }
      x0 = t.clientX; y0 = t.clientY; t0 = e.timeStamp; live = true;
    }
    function end(e) {
      if (!live) return;
      live = false;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (e.timeStamp - t0 > 800) return;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.8) return;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const back = rtl ? dx < 0 : dx > 0;
      setCurrentMonth(m => (back ? subMonths(m, 1) : addMonths(m, 1)));
    }
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    return () => {
      releaseSwipe('month');
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
    };
  }, []);

  // Fetch which days this month have events
  useEffect(() => {
    let cancelled = false;
    const monthStr = format(currentMonth, 'yyyy-MM');
    // Show the dots of a month already seen straight away, then refresh.
    const cached = monthCountsCache.get(monthStr);
    if (cached) setEventCounts(cached);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get('/events/month', { params: { month: monthStr, tz } })
      .then(res => {
        if (cancelled) return;
        monthCountsCache.set(monthStr, res.data || {});
        setEventCounts(res.data || {});
      })
      .catch(() => {}); // fail silently — counts are decorative
    return () => { cancelled = true; };
  }, [currentMonth]);

  return (
    <div className="card" style={{
      minHeight: 'calc(100dvh - 100px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      padding: 0,
    }}>
      {/* Green header */}
      <div style={{
        background: 'var(--color-primary-dark)',
        borderRadius: 'var(--radius) var(--radius) 0 0',
        padding: '10px 16px',
        // Centred, now that nothing sits either side of it: with the arrows
        // gone and space-between still on, the name had slid to the left edge.
        // A name in the middle of an empty bar is also the clearest invitation
        // to drag it — Silvia: "lascia il nome del mese centrato nella riga per
        // lo swipe intuitivo".
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        flexShrink: 0,
      }}>
        {/* No arrows here either, for the same reason as the day and the week:
            a sideways drag moves the month. The hidden buttons stay for
            TalkBack, which takes the swipe for itself. */}
        <button className="visually-hidden" onClick={() => setCurrentMonth(m => subMonths(m, 1))}>{A.prevMonth}</button>
        {!isSameMonth(currentMonth, new Date()) && (
          <button
            type="button"
            onClick={() => setCurrentMonth(startOfMonth(new Date()))}
            aria-label={T.nav.today}
            title={T.nav.today}
            style={{
              position: 'absolute', insetInlineStart: 10, top: '50%', transform: 'translateY(-50%)',
              width: 30, height: 30, borderRadius: '50%',
              border: '1.5px solid rgba(255,255,255,0.5)', background: 'transparent', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0,
            }}
          >
            <span className="rtl-flip" style={{ display: 'flex' }}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            </span>
          </button>
        )}
        {/* The same 0.92rem as the date in Oggi and the week's own heading —
            it was the only one still at 1rem. */}
        <span style={{ fontWeight: 800, fontSize: '0.92rem', textTransform: 'capitalize', color: 'white' }}>
          {format(currentMonth, 'MMMM yyyy', { locale })}
        </span>
        <button className="visually-hidden" onClick={() => setCurrentMonth(m => addMonths(m, 1))}>{A.nextMonth}</button>
      </div>

      {/* Dots or titles. Silvia: "a me piace molto la nostra ma sarebbe
          possibile inserire un pulsante che permetta di scegliere?" — so it is
          a choice, not a replacement, and it is remembered. Ours answers
          "which days are busy?" at a glance; the other answers "what is on the
          14th?" without a tap. Both are the right question, on different days. */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 16px 0', flexShrink: 0 }}>
        {[false, true].map(v => {
          const W = VIEW_WORDS[userLang] || VIEW_WORDS[String(userLang).split('-')[0]] || VIEW_WORDS.en;
          const on = dense === v;
          return (
            <button
              key={String(v)}
              type="button"
              aria-pressed={on}
              onClick={() => {
                setDense(v);
                try { localStorage.setItem('memo_month_dense', v ? '1' : '0'); } catch (_) {}
              }}
              style={{
                flex: 1, minWidth: 0, minHeight: 30, padding: '4px 6px',
                fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: on ? 800 : 500,
                borderRadius: 8, cursor: 'pointer',
                background: on ? 'var(--color-primary-light)' : 'var(--color-card)',
                color: 'var(--color-text)',
                border: on ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
              }}
            >
              {v ? W.titles : W.dots}
            </button>
          );
        })}
      </div>

      {/* Day-of-week headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '6px 8px 2px', flexShrink: 0 }}>
        {dayHeaders.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-text-muted)' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridTemplateRows: `repeat(${numRows}, ${dense ? 86 : 46}px)`,
        flexShrink: 0,
        padding: '0 4px',
      }}>
        {days.map(day => {
          const inMonth  = isSameMonth(day, currentMonth);
          const todayDay = isToday(day);
          const selected = selectedObj && isSameDay(day, selectedObj);
          const dateStr  = format(day, 'yyyy-MM-dd');
          const count    = inMonth ? (eventCounts[dateStr] || 0) : 0;
          const dayEvts  = dense ? (monthEvents[dateStr] || []) : [];

          if (dense) {
            // Three fit; the fourth becomes "…". Google shows a count, but a
            // count is the "+3" we just took out of the panel below — it names
            // a quantity you cannot act on. The dots say "there is more here"
            // and the day underneath says what.
            const shown = dayEvts.slice(0, 3);
            return (
              <div
                key={day.toString()}
                onClick={() => { if (selected) onGoToDay?.(); else onSelectDate(dateStr); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                  gap: 1, cursor: 'pointer', overflow: 'hidden',
                  padding: '2px 2px 0',
                  borderRadius: 6,
                  background: selected ? 'rgba(122,158,126,0.10)' : 'transparent',
                  opacity: inMonth ? 1 : 0.4,
                }}
              >
                <div style={{
                  alignSelf: 'center',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  fontSize: '0.72rem', fontWeight: todayDay ? 800 : 500,
                  background: todayDay ? 'var(--color-primary)' : 'transparent',
                  color: todayDay ? '#fff' : inMonth ? 'var(--color-text)' : 'var(--color-text-muted)',
                }}>
                  {format(day, 'd')}
                </div>
                {shown.map(ev => (
                  <div
                    key={ev.id}
                    translate="no"
                    title={stripIcon(ev.title)}
                    style={{
                      fontSize: '0.5rem', lineHeight: 1.25, fontWeight: 600,
                      padding: '1px 3px', borderRadius: 3,
                      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                      color: 'var(--color-text)',
                      background: ev.subCalendar?.color
                        ? `color-mix(in srgb, ${ev.subCalendar.color} 45%, var(--color-card))`
                        : ev.priority === 'red'    ? 'color-mix(in srgb, var(--color-red) 30%, var(--color-card))'
                        : ev.priority === 'yellow' ? 'color-mix(in srgb, var(--color-yellow) 34%, var(--color-card))'
                        : 'color-mix(in srgb, var(--color-green) 30%, var(--color-card))',
                    }}
                  >
                    {stripIcon(ev.title) || '—'}
                  </div>
                ))}
                {dayEvts.length > 3 && (
                  <div aria-hidden="true" style={{
                    fontSize: '0.55rem', lineHeight: 1, textAlign: 'center',
                    color: 'var(--color-text-muted)',
                  }}>•••</div>
                )}
              </div>
            );
          }

          // One tap chooses the day, a second one opens it. The panel that
          // used to sit under the grid showed the chosen day's events in a
          // second, smaller copy of a card — Silvia asked for it to go and for
          // the day itself to be the way in. It saves the bottom third of the
          // busiest screen in MeMo, and the day you open is the real one, with
          // everything a day has.
          return (
            <div
              key={day.toString()}
              onClick={() => { if (selected) onGoToDay?.(); else onSelectDate(dateStr); }}
              style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 2, cursor: 'pointer',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: '50%',
                fontSize: '0.88rem',
                fontWeight: todayDay ? 700 : 400,
                background: todayDay
                  ? 'var(--color-primary)'
                  : selected ? 'rgba(122,158,126,0.15)' : 'transparent',
                color: todayDay ? 'white' : inMonth ? 'var(--color-text)' : 'var(--color-text-muted)',
                opacity: inMonth ? 1 : 0.3,
                border: todayDay
                  ? 'none'
                  : selected
                  ? '1.5px solid var(--color-primary)'
                  : count > 0
                  ? '1.5px solid var(--color-primary)'
                  : 'none',
              }}>
                {format(day, 'd')}
              </div>
              {/* Event count — shown only when > 0 for days in current month */}
              <div style={{
                fontSize: '0.58rem', fontWeight: 700, lineHeight: 1,
                color: todayDay ? 'var(--color-primary)' : 'var(--color-text-muted)',
                visibility: count > 0 ? 'visible' : 'hidden',
                minHeight: '0.58rem',
              }}>
                {count}
              </div>
            </div>
          );
        })}
      </div>

      {/* Events preview for selected date */}
      {selectedObj && (
        <div style={{
          borderTop: '1px solid var(--color-border)',
          padding: '10px 16px 16px',
          flex: 1,
          overflowY: 'auto',
        }}>
          {/* No date heading here any more. It said the name of the day you had
              just tapped, directly under the square you tapped it on, and it
              was the one thing Silvia asked to remove: "solo il nome del giorno
              all'inizio della tabella andava eliminato". The way into the day is
              the square itself, tapped a second time. */}

          {loading ? (
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>…</div>
          ) : events.length === 0 ? (
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{T.today.emptyTitle}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 260, overflowY: 'auto' }}>
              {events.map(ev => {
                let timeStr = '';
                if (ev.start) {
                  try {
                    timeStr = new Date(ev.start).toLocaleTimeString(userLang, {
                      hour: '2-digit', minute: '2-digit', hour12: use12h,
                    });
                  } catch {}
                }
                const dotColor = ev.priority === 'red'
                  ? 'var(--color-red)'
                  : ev.priority === 'yellow'
                  ? 'var(--color-yellow)'
                  : 'var(--color-green)';
                return (
                  <div
                    key={ev.id}
                    onClick={() => navigate(`/edit/${ev.id}`, { state: { event: ev } })}
                    style={{ background: ev.subCalendar?.color ? `color-mix(in srgb, ${ev.subCalendar.color} 25%, var(--color-card))` : 'transparent', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderRadius: 6, padding: '3px 4px', margin: '0 -4px', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-alt)'}
                    onMouseLeave={e => { e.currentTarget.style.background = ev.subCalendar?.color ? `color-mix(in srgb, ${ev.subCalendar.color} 25%, var(--color-card))` : 'transparent'; }}
                  >
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                    {timeStr && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>{timeStr}</span>
                    )}
                    {iconOf(ev.title) && <EventIcon icon={iconOf(ev.title)} size={13} />}
                    <span translate="no" style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {stripIcon(ev.title)}
                    </span>
                  </div>
                );
              })}
              {/* "+3" told you something was hidden and then hid it. On a
                   full day that is the day you most need to see. Silvia: "voglio
                   che si vedano tutti gli eventi di un giorno selezionato".
                   All of them now; if the day is very long the panel scrolls,
                   which is a thing you can act on, unlike a number. */}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
